/**
 * Ein gemeinsames Zeitraum-Modell für den ganzen Auswertung-Hub. Presets + freier Zeitraum, aufgelöst
 * zu {from, to, months}. Daraus leiten die Sektionen ab, was sie brauchen: Finanzen einen
 * FinancePeriod {from,to}, der Vertrieb eine Range mit Monatsleiste für den Trend.
 */

export type AuswertungPeriodId = 'this-month' | 'quarter' | 'ytd' | 'last12' | 'all' | 'custom';

export interface ResolvedPeriod {
  readonly id: AuswertungPeriodId;
  /** YYYY-MM-DD; null = keine Untergrenze („Alles"). */
  readonly from: string | null;
  /** YYYY-MM-DD (i. d. R. heute; bei „custom" das gewählte Ende). */
  readonly to: string;
  /** Monatsschlüssel YYYY-MM (ältester zuerst) für die Trend-Balken. */
  readonly months: readonly string[];
  readonly label: string;
}

export const AUSWERTUNG_PERIOD_PRESETS: readonly { readonly id: AuswertungPeriodId; readonly label: string }[] = [
  { id: 'this-month', label: 'Dieser Monat' },
  { id: 'quarter', label: 'Dieses Quartal' },
  { id: 'ytd', label: 'Dieses Jahr' },
  { id: 'last12', label: 'Letzte 12 Monate' },
  { id: 'all', label: 'Alles' },
];

export function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}
function monthKey(dateIso: string): string {
  return dateIso.slice(0, 7);
}
export function dateLabel(dateIso: string): string {
  const [year, month, day] = dateIso.split('-');
  return year && month && day ? `${day}.${month}.${year}` : dateIso;
}

/** Alle Monatsschlüssel (YYYY-MM) von fromKey bis toKey inklusive, ältester zuerst. */
export function monthsBetween(fromKey: string, toKey: string): string[] {
  let year = Number(fromKey.slice(0, 4));
  let month = Number(fromKey.slice(5, 7));
  const toYear = Number(toKey.slice(0, 4));
  const toMonth = Number(toKey.slice(5, 7));
  const keys: string[] = [];
  let guard = 0;
  while ((year < toYear || (year === toYear && month <= toMonth)) && guard < 240) {
    keys.push(`${String(year).padStart(4, '0')}-${pad2(month)}`);
    month += 1;
    if (month > 12) { month = 1; year += 1; }
    guard += 1;
  }
  return keys;
}

/** Startmonat für ein Fenster von `count` Monaten, das mit `todayIso` endet. */
function windowStartKey(todayIso: string, count: number): string {
  let year = Number(todayIso.slice(0, 4));
  let month = Number(todayIso.slice(5, 7)) - (count - 1);
  while (month <= 0) { month += 12; year -= 1; }
  return `${String(year).padStart(4, '0')}-${pad2(month)}`;
}

export function resolvePeriod(
  id: AuswertungPeriodId,
  today: string,
  customFrom?: string,
  customTo?: string,
): ResolvedPeriod {
  const year = Number(today.slice(0, 4));
  const month = Number(today.slice(5, 7));
  const to = today;
  const toKey = monthKey(to);

  if (id === 'this-month') {
    const from = `${year}-${pad2(month)}-01`;
    return { id, from, to, months: [monthKey(from)], label: 'Dieser Monat' };
  }
  if (id === 'quarter') {
    const quarterStart = Math.floor((month - 1) / 3) * 3 + 1;
    const from = `${year}-${pad2(quarterStart)}-01`;
    return { id, from, to, months: monthsBetween(monthKey(from), toKey), label: 'Dieses Quartal' };
  }
  if (id === 'ytd') {
    const from = `${year}-01-01`;
    return { id, from, to, months: monthsBetween(`${year}-01`, toKey), label: 'Dieses Jahr' };
  }
  if (id === 'custom') {
    const from = customFrom || `${year}-01-01`;
    const customEnd = customTo || to;
    return { id, from, to: customEnd, months: monthsBetween(monthKey(from), monthKey(customEnd)), label: `${dateLabel(from)} – ${dateLabel(customEnd)}` };
  }
  if (id === 'all') {
    // Kennzahlen ohne Untergrenze; für die Monatsleiste trotzdem die letzten 12 Monate.
    return { id, from: null, to, months: monthsBetween(windowStartKey(to, 12), toKey), label: 'Alles' };
  }
  // last12 (Default)
  const startKey = windowStartKey(to, 12);
  return { id: 'last12', from: `${startKey}-01`, to, months: monthsBetween(startKey, toKey), label: 'Letzte 12 Monate' };
}

/** FinancePeriod-kompatibles {from,to}: leere Untergrenze = unbounded. */
export function financeRangeOf(period: ResolvedPeriod): { readonly from: string; readonly to: string } {
  return { from: period.from ?? '', to: period.to };
}
